import { useCallback } from 'react'
import { useSetSidePanelOpen } from '@renderer/stores/side-panel'
import { useBulkDeleteInsights, useBulkPatchInsights, useUpdateInsight } from './insights'
import { Insight } from '@shared/utils/insights/types.ts'

export interface UseInsightActionsOptions {
  /**
   * Whether to close the side panel after certain actions
   */
  closeSidePanelOnActions?: boolean

  /**
   * Source identifier for analytics tracking
   */
  analyticsSource?: string
}

export function useInsightActions(options: UseInsightActionsOptions = {}) {
  const { mutate: deleteInsights } = useBulkDeleteInsights()
  const { mutate: patchInsights } = useBulkPatchInsights()
  const { mutate: updateInsightMutation } = useUpdateInsight()
  const { closeSidePanelOnActions = false, analyticsSource = 'unknown' } = options
  const setSidePanelOpen = useSetSidePanelOpen()

  const bulkDeleteInsights = useCallback(
    (insightIds: string[]) => {
      deleteInsights(insightIds)
    },
    [deleteInsights],
  )

  const bulkPatchInsights = useCallback(
    (insightIds: string[], updates: Partial<Insight>) => {
      patchInsights({
        ids: insightIds,
        updates,
      })
    },
    [patchInsights],
  )

  const deleteInsight = useCallback(
    (insightId: string) => {
      deleteInsights([insightId])
      if (closeSidePanelOnActions) {
        setSidePanelOpen(false)
      }
    },
    [analyticsSource, closeSidePanelOnActions, setSidePanelOpen],
  )

  const updateInsight = useCallback(
    (
      insightId: string,
      updates: { insight?: string; assignedBy?: string; status?: 'active' | 'completed'; sentimentScore?: number },
    ) => {
      updateInsightMutation({ insightId, updates })
    },
    [],
  )

  const setInsightStatus = useCallback(
    (insightId: string, status: 'active' | 'completed') => {
      updateInsightMutation({ insightId, updates: { status } })
    },
    [updateInsightMutation],
  )

  const setInsightSentiment = useCallback(
    (insightId: string, sentiment: 'positive' | 'negative') => {
      updateInsightMutation({ insightId, updates: { sentimentScore: sentiment === 'positive' ? 1 : -1 } })
    },
    [updateInsightMutation],
  )

  return {
    deleteInsight,
    bulkDeleteInsights,
    bulkPatchInsights,
    setInsightStatus,
    updateInsight,
    setInsightSentiment,
  }
}
